import React, { useState } from 'react';
import { Download, FileText, Archive, CheckSquare } from 'lucide-react';

export default function ExportPanel() {
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [exportFormat, setExportFormat] = useState('pdf_package');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeAuditTrail, setIncludeAuditTrail] = useState(true);

  const documentCategories = [
    {
      id: 'environmental',
      name: 'Environmental Permits',
      count: 12,
      documents: [
        'Environmental Clearance Certificate',
        'Air Quality Compliance Report',
        'Water Discharge Permit',
        'Hazardous Waste Authorization'
      ]
    },
    {
      id: 'safety',
      name: 'Safety Certifications',
      count: 8,
      documents: [
        'Fire Safety Certificate',
        'Electrical Safety Inspection',
        'Building Safety Compliance',
        'Emergency Response Certification'
      ]
    },
    {
      id: 'operational',
      name: 'Operational Licenses',
      count: 6,
      documents: [
        'Factory License',
        'Trade License',
        'Boiler Operation Permit',
        'Lift Safety Certificate'
      ]
    },
    {
      id: 'quality',
      name: 'Quality Standards',
      count: 5,
      documents: [
        'ISO 45001 Certificate',
        'ISO 14001 Certificate',
        'Quality Management Certification',
        'Product Safety Compliance'
      ]
    }
  ];

  const exportTemplates = [
    {
      id: 'audit_ready',
      name: 'Audit-Ready Package',
      description: 'Complete document package with metadata and audit trails',
      includes: ['All documents', 'Metadata', 'Audit trail', 'Compliance checklist']
    },
    {
      id: 'regulatory_submission',
      name: 'Regulatory Submission',
      description: 'Documents formatted for regulatory body submission',
      includes: ['Required documents', 'Submission forms', 'Evidence files']
    },
    {
      id: 'internal_review',
      name: 'Internal Review Package',
      description: 'Documents for internal compliance review',
      includes: ['Active documents', 'Status reports', 'Renewal tracking']
    }
  ];

  const handleCategoryToggle = (categoryId) => {
    if (selectedDocuments?.includes(categoryId)) {
      setSelectedDocuments(selectedDocuments?.filter(id => id !== categoryId));
    } else {
      setSelectedDocuments([...selectedDocuments, categoryId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedDocuments?.length === documentCategories?.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documentCategories?.map(cat => cat?.id));
    }
  };

  const getTotalDocuments = () => {
    return documentCategories
      ?.filter(cat => selectedDocuments?.includes(cat?.id))
      ?.reduce((sum, cat) => sum + cat?.count, 0);
  };

  return (
    <div className="space-y-6">
      {/* Export Templates */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {exportTemplates?.map((template) => (
            <div
              key={template?.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start gap-3 mb-3">
                <Archive className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{template?.name}</h4>
                  <p className="text-sm text-gray-600">{template?.description}</p>
                </div>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                {template?.includes?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckSquare size={14} className="text-green-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                Use Template
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Document Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Select Documents</h3>
          <button
            onClick={handleSelectAll}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {selectedDocuments?.length === documentCategories?.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="space-y-3">
          {documentCategories?.map((category) => (
            <div
              key={category?.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={selectedDocuments?.includes(category?.id)}
                  onChange={() => handleCategoryToggle(category?.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{category?.name}</h4>
                  <p className="text-sm text-gray-600">{category?.count} documents</p>
                </div>
              </div>
              {selectedDocuments?.includes(category?.id) && (
                <div className="ml-7 space-y-2 text-sm text-gray-600">
                  {category?.documents?.map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <FileText size={14} className="text-blue-600" />
                      <span>{doc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Options</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Export Format
            </label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pdf_package">PDF Package (Recommended)</option>
              <option value="zip_archive">ZIP Archive</option>
              <option value="individual_pdfs">Individual PDFs</option>
              <option value="excel_report">Excel Report with Links</option>
            </select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="metadata"
                checked={includeMetadata}
                onChange={(e) => setIncludeMetadata(e?.target?.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="metadata" className="text-sm text-gray-700">
                Include document metadata and compliance information
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="audit"
                checked={includeAuditTrail}
                onChange={(e) => setIncludeAuditTrail(e?.target?.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="audit" className="text-sm text-gray-700">
                Include audit trail records
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Export Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Export Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-sm text-blue-700 mb-1">Categories</p>
            <p className="text-2xl font-bold text-blue-900">{selectedDocuments?.length}</p>
          </div>
          <div>
            <p className="text-sm text-blue-700 mb-1">Total Documents</p>
            <p className="text-2xl font-bold text-blue-900">{getTotalDocuments()}</p>
          </div>
          <div>
            <p className="text-sm text-blue-700 mb-1">Format</p>
            <p className="text-sm font-medium text-blue-900">
              {exportFormat?.split('_')?.map(word => 
                word?.charAt(0)?.toUpperCase() + word?.slice(1)
              )?.join(' ')}
            </p>
          </div>
          <div>
            <p className="text-sm text-blue-700 mb-1">Estimated Size</p>
            <p className="text-sm font-medium text-blue-900">~{getTotalDocuments() * 2.5} MB</p>
          </div>
        </div>
        <button
          disabled={selectedDocuments?.length === 0}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedDocuments?.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Download size={20} />
          Generate Export Package
        </button>
      </div>

      {/* Export Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="text-gray-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">Export Capabilities</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Document packages for regulatory submissions</li>
              <li>Audit preparation with complete compliance records</li>
              <li>Custom export configurations for different purposes</li>
              <li>Automated metadata extraction and formatting</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}